import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import DynamicDrawer from "../../../UI/Drawer/DynamicDrawer";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { setEsgComplianceSelectedTask } from "../../../../actions/task/esgCompliance/esgComplianceTaskAction";
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../../../actions/esgCompliance/esgComplianceAction";

// MUI Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GroupIcon from "@mui/icons-material/Group";
import MessageIcon from "@mui/icons-material/Message";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";

const NotificationDrawer = ({
  open,
  onClose,
  notifications = [],
  unreadCount = 0,
  isLoading = false,
  onTabChange,
  currentTab = 0,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  // Scroll to bottom when loading more finishes to show newly loaded content
  useEffect(() => {
    if (
      !isLoadingMore &&
      scrollContainerRef.current &&
      notifications.length > 0
    ) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [isLoadingMore]);

  // Handle View More click
  const handleLoadMoreClick = () => {
    if (onLoadMore) {
      onLoadMore();
    }
  };

  // Helper icons mapping
  const statusIcons = {
    approval: (
      <AssignmentIcon
        style={{ color: theme.palette.warning.main, fontSize: 20 }}
      />
    ),
    success: (
      <CheckCircleIcon
        style={{ color: theme.palette.success.main, fontSize: 20 }}
      />
    ),
    error: (
      <CancelIcon style={{ color: theme.palette.error.main, fontSize: 20 }} />
    ),
    role: (
      <GroupIcon style={{ color: theme.palette.info.main, fontSize: 20 }} />
    ),
    info: (
      <MessageIcon style={{ color: theme.palette.info.main, fontSize: 20 }} />
    ),
    mention: (
      <AlternateEmailIcon
        style={{ color: theme.palette.secondary.main, fontSize: 20 }}
      />
    ),
  };

  // Format time using moment
  const formatTime = (time) => {
    if (!time) return "";
    const now = moment();
    const notificationTime = moment(time);
    const diffMinutes = now.diff(notificationTime, "minutes");
    const diffHours = now.diff(notificationTime, "hours");
    const diffDays = now.diff(notificationTime, "days");

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60)
      return `${diffMinutes} min${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return moment(time).format("DD MMMM YYYY");
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    
    // Mark notification as read if it's unread
    if (!notification.isRead && notification._id) {
      try {
        await markNotificationAsRead({ notification_id: notification._id });
        
        // Invalidate queries to update badge count and notification list
        queryClient.invalidateQueries(["notificationsUnreadCount"]);
        queryClient.invalidateQueries(["notificationsList"]);
      } catch (error) {
        console.error("❌ Failed to mark notification as read:", error);
      }
    }

    // // DataHub KPI_ROLLUP notifications - redirect to Data Sets tab in DataHub module
    // if (notification.specialParam === "KPI_ROLLUP") {
    //   console.log(
    //     "📊 DataHub KPI_ROLLUP notification - navigating to Data Sets tab"
    //   );
    //   navigate("/data_hub", {
    //     state: {
    //       dataHubTab: 4, // Data Sets tab index
    //     },
    //   });
    //   onClose();
    //   return;
    // }

    // DataHub KPI notifications - redirect to Datahub_KPI_Survey with appropriate tab
    // if (notification.specialParam === "KPI_USER") {
    //   console.log(
    //     "📊 DataHub KPI notification - navigating to Datahub_KPI_Survey"
    //   );
    //   navigate("/task/Datahub_KPI_Survey", {
    //     state: {
    //       survey_id: notification.survey_id,
    //       assigned_as: notification?.assigned_as,
    //       autoOpenSurvey: true,
    //     },
    //   });
    //   onClose();
    //   return;
    // }

    // if (notification?.type === "Mention" && notification.redirect_url) {

    if (notification.redirect_url) {
      // Check if this is a TASK_USER mention (from task notification)
      // const isTaskUser = notification.sender_special_param === "TASK_USER";

      // if (isTaskUser) {
      //   // Navigate to task list with auto-open task ID
      //   console.log("📋 TASK_USER mention - navigating to task list");
      //   // navigate("/task/ESG_Compliance", {
      //   //   state: {
      //   //     autoOpenTaskId: notification?.assessment_id,
      //   //     autoClickMetric: notification?.question_id,
      //   //     autoClickType: "CHAT",
      //   //   },
      //   // });
      //   navigate(notification?.redirect_url);
      //   onClose();
      //   return;
      // }

      // // Regular ESG compliance mention
      // const taskData = {
      //   assessment_id: notification.assessment_id,
      //   assessment_name: notification.task_name,
      //   framework_id: notification.framework_id,
      //   template_type: notification.template_type || 'template_1', // Default template
      //   year_of_assessment: notification.year_of_assessment || '',
      //   assigned_as: ['CONTRIBUTOR'] || notification.assigned_as || [],
      // };

      // console.log('📤 Dispatching to Redux:', taskData);
      // console.log('🔗 Navigating to:', notification.action_url);
      // console.log('📝 Auto-open question ID:', notification.question_id);

      // // Set Redux state with task data before navigation
      // dispatch(setEsgComplianceSelectedTask(taskData));

      // Navigate to the task page with state for auto-opening question and chat
      // navigate(notification.action_url, {
      //   state: {
      //     autoOpenQuestion: notification.question_id,
      //     autoClickType: "CHAT",
      //     autoClickMetric: notification.question_id,
      //     assessment_id: notification.assessment_id,
      //   },
      // });
      navigate(notification?.redirect_url);
      onClose();
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    setMarkAsReadLoading(true);
    try {
      const response = await markAllNotificationsAsRead();
      
      // Invalidate queries to update badge count and notification list
      await queryClient.invalidateQueries(["notificationsUnreadCount"]);
      await queryClient.invalidateQueries(["notificationsList"]);
    } catch (error) {
      console.error("❌ Failed to mark all as read:", error);
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  // Get icon based on notification icon type
  const getNotificationIcon = (iconType) => {
    return statusIcons[iconType] || statusIcons.info;
  };

  // Highlight @mentions in message text
  const highlightMentions = (text) => {
    if (!text) return text;
    // Replace @username with styled span
    return text.replace(
      /@(\w+)/g,
      '<span style="color: #1976d2; font-weight: 600; background-color: rgba(25, 118, 210, 0.08); padding: 2px 4px; border-radius: 4px;">@$1</span>'
    );
  };

  // Tabs configuration
  const tabs = [
    { label: "All", value: 0 },
    { label: `Unread (${unreadCount})`, value: 1 },
    { label: "Mentions", value: 2 },
  ];

  const handleTabChange = (event, newValue) => {
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  // Secondary Header with Tabs
  const SecondaryHeader = (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
          },
          "& .Mui-selected": {
            color: theme.palette.primary.main,
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
    </Box>
  );

  // Custom action button - Mark all as read
  const CustomActionButton = (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "480px" }}>
      <Button
        variant="text"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 15,
          color: theme.palette.primary.main,
        }}
        disabled={unreadCount === 0 || markAsReadLoading}
        onClick={handleMarkAllAsRead}
      >
        {markAsReadLoading ? (
          <CircularProgress size={20} sx={{ mr: 1 }} />
        ) : null}
        {markAsReadLoading ? "Marking..." : "Mark all as read"}
      </Button>
    </Box>
  );

  return (
    <DynamicDrawer
      heading="Notification Center"
      hasDrawerOpen={open}
      handleClose={onClose}
      hasDefaultActionButton={false}
      hasCustomActionButton={CustomActionButton}
      hasSecondaryHeader={SecondaryHeader}
      customWidth="100%"
      contentHeight="calc(100vh - 200px)"
      noShadowForHeading={false}
      sx={{
        width: "480px !important",
        minWidth: "480px !important",
        maxWidth: "480px !important",
      }}
    >
      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && notifications.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
          }}
        >
          <MessageIcon
            sx={{ fontSize: 60, color: theme.palette.grey[400], mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary">
            No notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You're all caught up!
          </Typography>
        </Box>
      )}

      {/* Notifications List */}
      {!isLoading && notifications.length > 0 && (
        <Box
          ref={scrollContainerRef}
          sx={{
            py: 1,
            maxHeight: "calc(100vh - 280px)",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[300],
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: theme.palette.grey[400],
            },
          }}
        >
          {notifications.map((notification, index) => (
            <React.Fragment key={notification._id || index}>
              <Box
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  display: "flex",
                  gap: 2,
                  py: 2,
                  px: 1.5,
                  cursor: notification.action_url ? "pointer" : "default",
                  borderRadius: 2,
                  transition: "background-color 0.2s",
                  backgroundColor: !notification.isRead
                    ? theme.palette.primary.lighter
                    : "transparent",
                  "&:hover": {
                    backgroundColor: notification.action_url
                      ? theme.palette.action.hover
                      : "transparent",
                  },
                }}
              >
                {/* Icon Box */}
                <Box
                  sx={{
                    height: 42,
                    width: 42,
                    borderRadius: 3,
                    background: theme.palette.primary.lighter,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {getNotificationIcon(notification.icon)}
                </Box>

                {/* Text Content */}
                <Box sx={{ flex: 1 }}>
                  {/* Type Chip */}
                  <Chip
                    label={notification.type}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: 12,
                      fontWeight: 500,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.grey[300]}`,
                      color: theme.palette.text.primary,
                    }}
                  />

                  {/* Message */}
                  <Typography
                    component="div"
                    sx={{
                      mt: 1,
                      fontSize: 14,
                      fontWeight: !notification.isRead ? 600 : 400,
                      color: theme.palette.text.primary,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      "& p": { margin: 0, display: "inline" }, // Remove p tag margins
                      "& b": { fontWeight: 700 }, // Bold text
                      "& strong": { fontWeight: 700 }, // Strong text
                      "& i": { fontStyle: "italic" }, // Italic text
                      "& em": { fontStyle: "italic" }, // Emphasis text
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightMentions(notification.message),
                    }}
                  />

                  {/* Task and Metric Info */}
                  {(notification.task_name || notification.metric_name) && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        mt: 0.5,
                      }}
                    >
                      {notification.task_name && `📋 ${notification.task_name}`}
                      {notification.task_name &&
                        notification.metric_name &&
                        " • "}
                      {notification.metric_name &&
                        `📊 ${notification.metric_name}`}
                    </Typography>
                  )}

                  {/* Time */}
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.palette.grey[600],
                      mt: 1,
                    }}
                  >
                    {formatTime(notification.time)}
                  </Typography>
                </Box>

                {/* Unread Dot */}
                {!notification.isRead && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: theme.palette.primary.main,
                      mt: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>

              {/* Divider */}
              {index !== notifications.length - 1 && (
                <Divider sx={{ my: 0.5 }} />
              )}
            </React.Fragment>
          ))}

          {/* View More Button */}
          {hasMore && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadMoreClick}
                disabled={isLoadingMore}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.light,
                  },
                  "&:disabled": {
                    borderColor: theme.palette.grey[300],
                    color: theme.palette.grey[400],
                  },
                }}
              >
                {isLoadingMore ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading...
                  </>
                ) : (
                  "View more notifications"
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </DynamicDrawer>
  );
};

export default NotificationDrawer;
